import { ItemTypeConsts, ItemTypeRoutes } from 'client/util/Constants';
import {
  defaultOnboardingFeatures,
  implementationChecklistTasks,
  onboardingFeatureDisplayNames,
  onboardingFeatures,
} from 'client/GrowthEngineering/utilities/GrowthConstants';
import { featureFlags } from 'client/store/modules/FeatureFlagsModule';
import KbArticleUrls from 'client/util/Constants/KbArticleUrls';

export const tasks = {
  [implementationChecklistTasks.createUser]: {
    defaultUrl: ItemTypeRoutes[ItemTypeConsts.systemUser],
    headerText: 'Create a user',
    tooltip: {
      href: KbArticleUrls.gettingStartedUsers,
      title: 'Getting Started: Users',
    },
  },
  [implementationChecklistTasks.createDevice]: {
    defaultUrl: ItemTypeRoutes[ItemTypeConsts.device],
    headerText: 'Add a device',
    tooltip: {
      href: KbArticleUrls.gettingStartedDevices,
      title: 'Getting Started: Devices',
    },
  },
  [implementationChecklistTasks.createServer]: {
    defaultUrl: ItemTypeRoutes[ItemTypeConsts.device],
    headerText: 'Add a server',
  },
  [implementationChecklistTasks.createUserGroup]: {
    defaultUrl: ItemTypeRoutes[ItemTypeConsts.userGroup],
    headerText: 'Create a user group',
    tooltip: {
      href: KbArticleUrls.gettingStartedGroups,
      title: 'Getting Started: Groups',
    },
  },
  [implementationChecklistTasks.bindUserToUserGroup]: {
    defaultUrl: ItemTypeRoutes[ItemTypeConsts.userGroup],
    headerText: 'Bind users to user group',
    tooltip: {
      href: KbArticleUrls.bindingUsersToUserGroup,
      title: 'Adding Users to the Group',
    },
  },
  [implementationChecklistTasks.bindUserToDevice]: {
    defaultUrl: `${ItemTypeRoutes[ItemTypeConsts.device]}`,
    headerText: 'Bind user to device',
    tooltip: {
      href: KbArticleUrls.bindingUsersToDevices,
      title: 'Binding Users to Devices',
    },
  },
  [implementationChecklistTasks.createRadius]: {
    defaultUrl: `${ItemTypeRoutes[ItemTypeConsts.radiusServer]}/new`,
    headerText: 'Create RADIUS server',
  },
  [implementationChecklistTasks.createPolicy]: {
    defaultUrl: ItemTypeRoutes[ItemTypeConsts.configuration],
    headerText: 'Create Policy (Recommended: Disable USB)',
    tooltip: {
      href: KbArticleUrls.creatingPolicies,
      title: 'Creating a Policy',
    },
  },
  [implementationChecklistTasks.bindPolicyToDevice]: {
    defaultUrl: ItemTypeRoutes[ItemTypeConsts.configuration],
    headerText: 'Bind policy to device',
    tooltip: {
      href: KbArticleUrls.assigningPolicyGroup,
      title: 'Assigning a Policy Group to a Device Group',
    },
  },
  [implementationChecklistTasks.createConditionalAccessPolicy]: {
    defaultUrl: '#/security/authnpolicies',
    headerText: 'Create a Conditional Access Policy requiring MFA for user group',
  },
  [implementationChecklistTasks.enableSSHPasswordLogin]: {
    headerText: 'Enable SSH Password Login (optional: Linux only)',
    tooltip: {
      href: KbArticleUrls.gettingStartedDevices,
      title: 'Getting Started: Devices',
    },
  },
  [implementationChecklistTasks.electLdapBindDnAccount]: {
    headerText: 'Elect LDAP BindDN account',
    tooltip: {
      href: KbArticleUrls.LdapKB,
      title: 'Using JumpCloud\'s LDAP-as-a-Service',
    },
  },
  [implementationChecklistTasks.configureToUseLdap]: {
    headerText: 'Configure resource to use JumpCloud LDAP',
    tooltip: {
      video: 'https://jumpcloud-1.wistia.com/medias/mojlsntsj8',
      title: 'Configuring LDAP',
    },
  },
  [implementationChecklistTasks.configureDeviceForRadius]: {
    hasManualCheck: true,
    headerText: 'Configure network device',
    tooltip: {
      href: KbArticleUrls.radiusDeviceConfiguration,
      title: 'Configuring a Wireless Access Point (WAP), VPN or Router for JumpCloud\'s RADIUS',
    },
  },
  [implementationChecklistTasks.bindGroupToRadius]: {
    headerText: 'Bind user group to RADIUS server',
    tooltip: {
      href: KbArticleUrls.connectingGroupToResources,
      title: 'Connecting the Group to Resources',
    },
  },
  [implementationChecklistTasks.createExampleSamlApp]: {
    defaultUrl: ItemTypeRoutes[ItemTypeConsts.sso],
    headerText: 'Create An Example SSO Application',
    tooltip: {
      text: 'Tip: Don\'t use a production SSO application because it can cause a loss of service. All users in the app must be fully activated in JumpCloud first.',
    },
  },
  [implementationChecklistTasks.bindGroupToSamlApp]: {
    headerText: 'Bind user group to SSO application',
    tooltip: {
      href: KbArticleUrls.gettingStartedApplications,
      title: 'Getting Started: Applications (SAML SSO)',
    },
  },
  [implementationChecklistTasks.configureDeviceEnrollmentForMdm]: {
    headerText: 'Configure automated device enrollment (optional)',
    tooltip: {
      href: KbArticleUrls.mdmDepConfigure,
      title: 'Configuring DEP',
    },
  },
  [implementationChecklistTasks.configureIdentityManagementForSaml]: {
    hasManualCheck: true,
    headerText: 'Configure Identity Management for the SSO application (optional)',
    tooltip: {
      href: KbArticleUrls.identityManagementConnectors,
      title: 'JumpCloud Identity Management Connectors',
    },
  },
  [implementationChecklistTasks.configureMdm]: {
    defaultUrl: ItemTypeRoutes[ItemTypeConsts.mdmSettings],
    headerText: 'Configure JumpCloud\'s Apple MDM',
    tooltip: {
      href: KbArticleUrls.gettingStartedMdm,
      title: 'Getting Started: MDM',
    },
  },
  [implementationChecklistTasks.configureVpp]: {
    headerText: 'Configure VPP (optional)',
    tooltip: {
      href: KbArticleUrls.mdmVpp,
      title: 'Setting Up Apple\'s VPP',
    },
  },
  [implementationChecklistTasks.createMdmEnrollmentPolcy]: {
    defaultUrl: `${ItemTypeRoutes[ItemTypeConsts.configuration]}/choose/darwin`,
    headerText: 'Create JumpCloud Apple MDM Enrollment policy (optional)',
    tooltip: {
      href: KbArticleUrls.mdmEnrollmentPolicy,
      title: 'Creating a MacOS MDM Enrollment Policy',
    },
  },
  [implementationChecklistTasks.bindMdmEnrollmentPolicy]: {
    headerText: 'Bind JumpCloud MDM Enrollment policy to macOS device (optional)',
  },
  [implementationChecklistTasks.approveMdmProfile]: {
    hasManualCheck: true,
    headerText: 'User approves MDM profile on macOS device (optional)',
    tooltip: {
      href: KbArticleUrls.mdmProfileApproval,
      title: 'Approving an MDM Enrollment Profile',
    },
  },
  [implementationChecklistTasks.observeSystemInsights]: {
    defaultUrl: ItemTypeRoutes[ItemTypeConsts.device],
    headerText: 'Observe System Insights',
    tooltip: {
      href: KbArticleUrls.systemInsightsView,
      title: 'How Do I Use and View System Insights Information?',
    },
  },
  [implementationChecklistTasks.observeDirectoryInsights]: {
    defaultUrl: '#/insights',
    headerText: 'Observe Directory Insights',
    tooltip: {
      href: KbArticleUrls.activityLogViewData,
      title: 'View Directory Insights Data in the Activity Log',
    },
  },
  [implementationChecklistTasks.navigateToPasswordManager]: {
    defaultUrl: ItemTypeRoutes[ItemTypeConsts.passwordManager],
    headerText: 'Navigate to Password Manager',
  },
  [implementationChecklistTasks.enrollUserViaUserGroups]: {
    headerText: 'Enroll User via User Groups',
  },
  [implementationChecklistTasks.launchRemoteAssist]: {
    defaultUrl: ItemTypeRoutes[ItemTypeConsts.deviceList],
    headerText: 'Select the device you want to provide remote assistance for, and click Launch Remote Assist.',
    tooltip: {
      href: KbArticleUrls.remoteAssist,
      title: 'Getting Started: Remote Assist',
    },
  },
  [implementationChecklistTasks.enterSessionCode]: {
    hasManualCheck: true,
    headerText: 'Enter session code on device',
  },
  [implementationChecklistTasks.viewCloudInsights]: {
    defaultUrl: ItemTypeRoutes[ItemTypeConsts.cloudInsights],
    headerText: 'View Cloud Insights for AWS applications (optional)',
    tooltip: {
      href: KbArticleUrls.cloudInsights,
      title: 'Getting Started: Cloud Insights',
    },
  },
};

export const implementationChecklists = {
  [defaultOnboardingFeatures.createAdminAccount]: {
    disabled: true,
    headerText: defaultOnboardingFeatures.createAdminAccount,
    tasks: [],
  },
  [onboardingFeatures.appleMdm]: {
    headerText: onboardingFeatureDisplayNames.appleMdm,
    tasks: [
      tasks.configureMdm,
      tasks.configureDeviceEnrollmentForMdm,
      tasks.configureVpp,
    ],
  },
  [onboardingFeatures.serverManagement]: {
    headerText: onboardingFeatureDisplayNames.serverManagement,
    tasks: [
      tasks.createUser,
      tasks.createServer,
      tasks.enableSSHPasswordLogin,
      tasks.bindUserToDevice,
    ],
  },
  [onboardingFeatures.ldap]: {
    headerText: onboardingFeatureDisplayNames.ldap,
    tasks: [
      tasks.createUser,
      tasks.createUserGroup,
      tasks.bindUserToUserGroup,
      tasks.electLdapBindDnAccount,
      tasks.configureToUseLdap,
    ],
  },
  [onboardingFeatures.radius]: {
    headerText: onboardingFeatureDisplayNames.radius,
    tasks: [
      tasks.createUser,
      tasks.createUserGroup,
      tasks.bindUserToUserGroup,
      tasks.createRadius,
      tasks.configureDeviceForRadius,
      tasks.bindGroupToRadius,
    ],
  },
  [onboardingFeatures.ssoAuth]: {
    headerText: onboardingFeatureDisplayNames.ssoAuth,
    tasks: [
      tasks.createUser,
      tasks.createUserGroup,
      tasks.createExampleSamlApp,
      tasks.bindGroupToSamlApp,
      tasks.viewCloudInsights,
    ],
  },
  [onboardingFeatures.deviceManagement]: {
    headerText: onboardingFeatureDisplayNames.deviceManagement,
    tasks: [
      tasks.createUser,
      tasks.createDevice,
      tasks.bindUserToDevice,
      tasks.createPolicy,
      tasks.bindPolicyToDevice,
    ],
  },
  [onboardingFeatures.mfa]: {
    headerText: onboardingFeatureDisplayNames.mfa,
    tasks: [
      tasks.createUser,
      tasks.createUserGroup,
      tasks.bindUserToUserGroup,
      tasks.createConditionalAccessPolicy,
    ],
  },
  [onboardingFeatures.userManagement]: {
    headerText: onboardingFeatureDisplayNames.userManagement,
    tasks: [
      tasks.createUser,
      tasks.createUserGroup,
      tasks.bindUserToUserGroup,
      tasks.createExampleSamlApp,
      tasks.bindGroupToSamlApp,
      tasks.configureIdentityManagementForSaml,
    ],
  },
  [onboardingFeatures.compliance]: {
    headerText: onboardingFeatureDisplayNames.compliance,
    tasks: [
      tasks.createUser,
      tasks.createDevice,
      tasks.createPolicy,
      tasks.bindPolicyToDevice,
      tasks.observeSystemInsights,
      tasks.observeDirectoryInsights,
    ],
  },
  [onboardingFeatures.passwordManagement]: {
    headerText: onboardingFeatureDisplayNames.passwordManagement,
    tasks: [
      tasks.createUser,
      tasks.bindUserToUserGroup,
      tasks.navigateToPasswordManager,
      tasks.enrollUserViaUserGroups,
    ],
  },
  [onboardingFeatures.remoteAssist]: {
    featureFlag: featureFlags.remoteAssistOnboarding,
    headerText: onboardingFeatureDisplayNames.remoteAssist,
    startDate: '2022-10-25',
    subText: 'Our cloud-based remote support solution gives IT Admins real-time access to JumpCloud-managed devices for troubleshooting, maintenance, and training purposes.',
    tasks: [
      tasks.createDevice,
      tasks.launchRemoteAssist,
      tasks.enterSessionCode,
    ],
  },
};

Object.keys(tasks).forEach(key => {
  tasks[key].taskType = key;
});

Object.keys(implementationChecklists).forEach(key => {
  implementationChecklists[key].feature = key;
});

export default {
  implementationChecklists,
  tasks,
};
